import { useEffect, useRef, useState, type ReactNode } from 'react';
import type { Direction } from './useNavigator';
import './navigation.css';

/** Ширина зоны у левого края, с которой начинается жест «назад» — как в iOS. */
const EDGE = 24;

/** Доля ширины экрана, после которой отпускание возвращает назад. */
const THRESHOLD = 0.4;

/**
 * Переходы между экранами и жест «назад» от левого края.
 *
 * Это единственное место, где веб-приложение вынуждено подражать системе руками:
 * ни перехода push/pop, ни свайпа «назад» браузер не даёт. Без них PWA читается как
 * сайт с первой же секунды — экран просто подменяется.
 *
 * Жест начинается **только** от левого края, как системный: иначе он спорил бы
 * со свайпами строк ([D-34](docs/DECISIONS.md)), которые живут в середине экрана.
 */
export function NavigationStack({
  path,
  direction,
  canGoBack,
  onBack,
  children,
}: {
  path: string;
  direction: Direction;
  canGoBack: boolean;
  onBack: () => void;
  children: ReactNode;
}) {
  const [previous, setPrevious] = useState<{ path: string; content: ReactNode } | null>(null);
  const shown = useRef<{ path: string; content: ReactNode }>({ path, content: children });
  const [drag, setDrag] = useState<number | null>(null);
  /*
   * Пройденное расстояние живёт в ссылке, а не только в состоянии.
   *
   * Решение «вернуться или нет» принимается в момент отпускания пальца, а состояние
   * к этому моменту может ещё не отрисоваться: быстрый жест успевает закончиться
   * раньше перерисовки, и в обработчике лежит старое значение. Поймано прогоном
   * 04.09.2026 — жест не срабатывал вовсе. Состояние остаётся только для того,
   * чтобы экран ехал за пальцем.
   */
  const distance = useRef(0);
  const gesture = useRef<{ x: number; y: number; tracking: boolean }>({
    x: 0,
    y: 0,
    tracking: false,
  });
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (shown.current.path !== path && direction !== 'none') {
      setPrevious(shown.current);

      // Уезжающий экран живёт ровно столько, сколько идёт анимация: держать его
      // дольше — значит держать в памяти список, который уже не виден.
      const timer = window.setTimeout(() => setPrevious(null), 280);
      shown.current = { path, content: children };

      return () => window.clearTimeout(timer);
    }
    shown.current = { path, content: children };
    return undefined;
  }, [path, direction, children]);

  const width = () => box.current?.getBoundingClientRect().width ?? 1;

  const onPointerDown = (event: React.PointerEvent) => {
    const rect = box.current?.getBoundingClientRect();
    if (!canGoBack || !rect || event.clientX - rect.left > EDGE) return;

    gesture.current = { x: event.clientX, y: event.clientY, tracking: true };
    distance.current = 0;
  };

  const onPointerMove = (event: React.PointerEvent) => {
    if (!gesture.current.tracking) return;

    const dx = event.clientX - gesture.current.x;
    const dy = event.clientY - gesture.current.y;

    // Вертикальное движение — это прокрутка, а не возврат: отдаём жест списку.
    if (drag === null && Math.abs(dy) > Math.abs(dx)) {
      gesture.current.tracking = false;
      return;
    }
    distance.current = Math.max(0, dx);
    setDrag(distance.current);
  };

  const finish = () => {
    if (!gesture.current.tracking) return;
    gesture.current.tracking = false;

    const passed = distance.current >= width() * THRESHOLD;
    distance.current = 0;
    setDrag(null);

    if (passed) onBack();
  };

  return (
    <div
      ref={box}
      className="stack"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={finish}
      onPointerCancel={finish}
    >
      {previous ? (
        <div
          key={previous.path}
          className={`stack__screen stack__screen--leaving-${direction}`}
          aria-hidden="true"
        >
          {previous.content}
        </div>
      ) : null}

      <div
        key={path}
        className={`stack__screen ${previous ? `stack__screen--entering-${direction}` : ''}`}
        style={
          drag === null
            ? undefined
            : { transform: `translateX(${drag}px)`, transition: 'none' }
        }
      >
        {children}
      </div>
    </div>
  );
}
